/* eslint-disable @typescript-eslint/no-explicit-any */
import { getRefreshToken, useOrders } from '@vgrubs/components';
import { SimpleBrandEntity } from 'components/src/types/brand.types';
import { SimpleLocationEntity } from 'components/src/types/location.types';
import { OrderBrand } from 'components/src/types/order.types';
import { LocationStatus } from 'components/src/types/shared.types';
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.NX_API_URL;

export const useOrdersSocket = ({
  from,
  to,
  locationIds = [],
  brandIds = [],
  status,
}: {
  from: string;
  to: string;
  locationIds: SimpleLocationEntity[];
  brandIds: (SimpleBrandEntity | OrderBrand)[];
  status: LocationStatus;
}) => {
  const lastSoundTimeRef = useRef(0);
  const socketRef = useRef<any>(null); // Store socket instance

  const {
    updateOrder,
    setHasNewOrder,
    removeOrderById,
    updateActiveOrderAndSort,
  } = useOrders((state) => ({
    dataOrders: state.dataOrders,
    updateOrder: state.updateOrder,
    setHasNewOrder: state.setHasNewOrder,
    removeOrderById: state.removeOrderById,
    updateActiveOrderAndSort: state.updateActiveOrderAndSort,
  }));

  const refreshToken = getRefreshToken();

  // ✅ Utility function to build the payload dynamically
  const buildFilters = () => {
    const filters: {
      from: string;
      to: string;
      subscribe: boolean;
      locationIds?: number[];
      brandIds?: number[];
    } = { from, to, subscribe: true };

    if (locationIds.length > 0)
      filters.locationIds = locationIds.map(({ id }) => id);

    if (brandIds.length > 0) filters.brandIds = brandIds.map(({ id }) => id);

    return filters;
  };

  // ✅ Connect to socket with refresh token
  useEffect(() => {
    if (!refreshToken) return;

    const socket = io(SOCKET_URL, {
      path: '/order',
      transports: ['websocket'],
      auth: {
        authorization: refreshToken, // Token in handshake
      },
    });

    socketRef.current = socket;

    socket.on('connection_successful', () => {

      // Emit filters right after connect
      const filters = buildFilters();
      socket.emit('observe-orders', filters);
    });

    // Listen for order update
    socket.on('observe-orders', (updatedOrder) => {
    });

    // Listen for new order
    socket.on('new_order', (data) => {
      if (status === 'active') {
        setHasNewOrder(true); // For UI alert
        playSound();
      }
    });

    // Listen for order update
    socket.on('status_change', (updatedOrder) => {
      if (status === 'active') {

        if (
          updatedOrder.status === 'completed' ||
          updatedOrder.status === 'cancelled'
        ) {
          removeOrderById(updatedOrder.id);
        } else if (updatedOrder.status === 'accepted') {
          updateActiveOrderAndSort(updatedOrder);
        } else {
          updateOrder(updatedOrder);
        }
      }
    });

    socket.on('connect_error', (err) => {
      console.error('Connection Error:', err.message);
      console.error('Error Details:', err);
    });

    // Clean up on unmount
    return () => {
      socket.disconnect();
    };
  }, [refreshToken]); // Connect once when refreshToken is available

  const ordersSocketEmit = () => {
    if (
      socketRef.current &&
      socketRef.current.connected &&
      status === 'active'
    ) {
      const filters = buildFilters();
      socketRef.current.emit('observe-orders', filters);
    }
  };

  // ✅ Dynamic filter update emit when filters change
  useEffect(() => {
    ordersSocketEmit();
  }, [from, to]); // Re-emit when any filter changes

  // ✅ Sound playing function with throttling
  function playSound() {
    const now = Date.now();
    const MIN_INTERVAL = 1000;
    if (now - lastSoundTimeRef.current > MIN_INTERVAL) {
      lastSoundTimeRef.current = now;
      const audio = new Audio('/sound3.mp3');
      audio.play().catch((e) => console.error('Audio play error:', e));
    }
  }

  return { ordersSocketEmit };
};
